import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Phone, MessageSquare, FileText, Camera, CheckCircle2, Sparkles, ShieldCheck, ChevronRight, Truck, Languages, Navigation, Radio, X, Maximize2, Copy } from 'lucide-react';
export function LiveTracking() {
  const [mapExpanded, setMapExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const driverPhone = '+971 50 312 8842';
  const copyPhone = () => {
    try {
      navigator.clipboard.writeText(driverPhone);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };
  return <div className="min-h-[calc(100vh-5rem)] bg-surface-100 pb-12" data-id="element-1428">
      {/* Top Map Banner */}
      <div className="h-72 w-full relative bg-gray-800 overflow-hidden" data-id="element-1429">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=1600')] bg-cover bg-center opacity-60" data-id="element-1430" />
        <div className="absolute inset-0 bg-gradient-to-t from-surface-100 via-transparent to-transparent" data-id="element-1431" />

        {/* Live tracking badge — top left */}
        <div className="absolute top-5 left-5 flex items-center gap-2 bg-red-500/95 backdrop-blur-sm text-white px-3 py-1.5 rounded-full shadow-lg" data-id="element-1432">
          <span className="relative flex h-2 w-2" data-id="element-1433">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" data-id="element-1434" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" data-id="element-1435" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-wider" data-id="element-1436">
            Live tracking
          </span>
        </div>

        {/* Expand map button — top right */}
        <button onClick={() => setMapExpanded(true)} className="absolute top-5 right-5 bg-white/95 hover:bg-white px-3 py-2 rounded-xl shadow-lg flex items-center gap-2 text-xs font-semibold text-gray-900 transition-colors" data-id="element-1437">
          <Maximize2 className="w-3.5 h-3.5" data-id="element-1438" />
          Full map
        </button>

        {/* Animated truck marker */}
        <div className="absolute top-[45%] left-[50%] -translate-x-1/2 -translate-y-1/2" data-id="element-1439">
          <div className="relative" data-id="element-1440">
            {/* Pulse ring */}
            <div className="absolute inset-0 w-16 h-16 -m-2 rounded-full bg-brand-500/30 animate-ping" data-id="element-1441" />
            {/* Truck pin */}
            <div className="relative w-12 h-12 rounded-full bg-white shadow-xl border-2 border-brand-500 flex items-center justify-center" data-id="element-1442">
              <Truck className="w-6 h-6 text-brand-600" data-id="element-1443" />
            </div>
            {/* Tooltip plate label */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap" data-id="element-1444">
              <div className="bg-gray-900 text-white text-[10px] font-bold px-2.5 py-1 rounded-md shadow-lg flex items-center gap-1.5" data-id="element-1445">
                <span className="text-brand-300" data-id="element-1446">DXB</span>
                <span data-id="element-1447">R · 84922</span>
              </div>
            </div>
          </div>
        </div>

        {/* ETA pill */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 glass-panel px-6 py-3 rounded-full flex items-center gap-4 shadow-xl" data-id="element-1448">
          <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" data-id="element-1449" />
          <span className="font-bold text-gray-900" data-id="element-1450">Arriving in 12 mins</span>
          <span className="text-gray-400" data-id="element-1451">|</span>
          <span className="text-gray-600 font-medium" data-id="element-1452">1.4 km away</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10" data-id="element-1453">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1454">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-2 space-y-6" data-id="element-1455">
            {/* Status Card */}
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100" data-id="element-1456">
              <div className="flex justify-between items-center mb-8" data-id="element-1457">
                <div data-id="element-1458">
                  <h2 className="text-2xl font-display font-bold text-gray-900" data-id="element-1459">
                    Job #PM-8842
                  </h2>
                  <p className="text-gray-500 mt-1" data-id="element-1460">
                    Home Moving • 3-Ton Truck
                  </p>
                </div>
                <div className="bg-brand-50 text-brand-600 px-4 py-2 rounded-full font-bold text-sm" data-id="element-1461">
                  In Transit
                </div>
              </div>

              {/* Timeline */}
              <div className="relative" data-id="element-1462">
                <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gray-100" data-id="element-1463" />
                <div className="space-y-6" data-id="element-1464">
                  <div className="relative flex gap-4" data-id="element-1465">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center shrink-0 z-10 shadow-sm border-2 border-white" data-id="element-1466">
                      <CheckCircle2 className="w-4 h-4 text-white" data-id="element-1467" />
                    </div>
                    <div data-id="element-1468">
                      <p className="font-bold text-gray-900" data-id="element-1469">Driver Assigned</p>
                      <p className="text-sm text-gray-500" data-id="element-1470">08:45 AM</p>
                    </div>
                  </div>
                  <div className="relative flex gap-4" data-id="element-1471">
                    <div className="w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center shrink-0 z-10 shadow-sm border-2 border-white" data-id="element-1472">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse" data-id="element-1473" />
                    </div>
                    <div data-id="element-1474">
                      <p className="font-bold text-gray-900" data-id="element-1475">
                        Heading to Pickup
                      </p>
                      <p className="text-sm text-brand-600 font-medium mt-1" data-id="element-1476">
                        ETA: 09:15 AM
                      </p>
                    </div>
                  </div>
                  <div className="relative flex gap-4 opacity-50" data-id="element-1477">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0 z-10 border-2 border-white" data-id="element-1478">
                      <div className="w-2 h-2 bg-gray-400 rounded-full" data-id="element-1479" />
                    </div>
                    <div data-id="element-1480">
                      <p className="font-bold text-gray-900" data-id="element-1481">Loading Started</p>
                      <p className="text-sm text-gray-500" data-id="element-1482">Pending</p>
                    </div>
                  </div>
                  <div className="relative flex gap-4 opacity-50" data-id="element-1483">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0 z-10 border-2 border-white" data-id="element-1484">
                      <MapPin className="w-4 h-4 text-gray-400" data-id="element-1485" />
                    </div>
                    <div data-id="element-1486">
                      <p className="font-bold text-gray-900" data-id="element-1487">Dropoff</p>
                      <p className="text-sm text-gray-500" data-id="element-1488">Downtown Dubai</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* DRIVER + VEHICLE CARD */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-1489">
              {/* Header strip */}
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between" data-id="element-1490">
                <div className="flex items-center gap-2" data-id="element-1491">
                  <span className="relative flex h-2 w-2" data-id="element-1492">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-1493" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" data-id="element-1494" />
                  </span>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-700" data-id="element-1495">
                    Live tracking active
                  </p>
                </div>
                <button onClick={() => setMapExpanded(true)} className="flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700" data-id="element-1496">
                  <Navigation className="w-3.5 h-3.5" data-id="element-1497" />
                  Track on map
                </button>
              </div>

              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1498">
                {/* DRIVER */}
                <div data-id="element-1499">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-3" data-id="element-1500">
                    Your driver
                  </p>
                  <div className="flex items-center gap-4 mb-4" data-id="element-1501">
                    <div className="relative" data-id="element-1502">
                      <img src="https://i.pravatar.cc/150?img=11" alt="Driver" className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md" data-id="element-1503" />
                      <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm" data-id="element-1504">
                        <ShieldCheck className="w-4 h-4 text-blue-500" data-id="element-1505" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1506">
                      <h3 className="text-lg font-bold text-gray-900 truncate" data-id="element-1507">
                        Ahmed Hassan
                      </h3>
                      <p className="text-xs text-gray-500" data-id="element-1508">
                        4.9 ★ · 342 trips · 6 yrs
                      </p>
                      <div className="flex items-center gap-1 mt-1 text-xs text-gray-500" data-id="element-1509">
                        <Languages className="w-3 h-3" data-id="element-1510" />
                        <span data-id="element-1511">Arabic · English · Hindi</span>
                      </div>
                    </div>
                  </div>

                  {/* Phone + call action */}
                  <div className="bg-green-50 border border-green-100 rounded-2xl p-3 flex items-center justify-between gap-3" data-id="element-1512">
                    <div className="min-w-0" data-id="element-1513">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-green-700 mb-0.5" data-id="element-1514">
                        Direct line
                      </p>
                      <div className="flex items-center gap-1.5" data-id="element-1515">
                        <p className="text-sm font-bold text-gray-900 truncate" data-id="element-1516">
                          {driverPhone}
                        </p>
                        <button onClick={copyPhone} className="text-gray-400 hover:text-gray-700 transition-colors shrink-0" aria-label="Copy phone" data-id="element-1517">
                          <Copy className="w-3 h-3" data-id="element-1518" />
                        </button>
                        {copied && <span className="text-[10px] font-semibold text-green-700" data-id="element-1519">
                            Copied
                          </span>}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0" data-id="element-1520">
                      <button className="w-10 h-10 rounded-full bg-white hover:bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-700 transition-colors shadow-sm" aria-label="Message" data-id="element-1521">
                        <MessageSquare className="w-4 h-4" data-id="element-1522" />
                      </button>
                      <a href={`tel:${driverPhone.replace(/\s/g, '')}`} className="w-10 h-10 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center text-white transition-colors shadow-md shadow-green-500/30" aria-label="Call driver" data-id="element-1523">
                        <Phone className="w-4 h-4" data-id="element-1524" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* VEHICLE */}
                <div data-id="element-1525">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-3" data-id="element-1526">
                    Vehicle
                  </p>
                  <div className="flex items-start gap-4 mb-4" data-id="element-1527">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 flex items-center justify-center shrink-0" data-id="element-1528">
                      <Truck className="w-8 h-8 text-gray-700" data-id="element-1529" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1530">
                      <h3 className="text-base font-bold text-gray-900 leading-tight" data-id="element-1531">
                        Isuzu N-Series
                      </h3>
                      <p className="text-xs text-gray-500" data-id="element-1532">3-Ton Box Truck</p>
                      <div className="flex items-center gap-2 mt-1.5" data-id="element-1533">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-1534">
                          Color
                        </p>
                        <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-2 py-0.5" data-id="element-1535">
                          <span className="w-2.5 h-2.5 rounded-full bg-white border border-gray-300" data-id="element-1536" />
                          <span className="text-[11px] font-semibold text-gray-800" data-id="element-1537">
                            White
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* UAE-style plate */}
                  <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm flex items-stretch" data-id="element-1538">
                    <div className="bg-gray-900 text-white px-2 py-2 flex flex-col items-center justify-center min-w-[52px]" data-id="element-1539">
                      <span className="text-[8px] font-bold tracking-wider leading-none" data-id="element-1540">
                        UAE
                      </span>
                      <span className="text-[9px] font-bold tracking-wider text-red-400 mt-0.5 leading-none" data-id="element-1541">
                        DUBAI
                      </span>
                    </div>
                    <div className="flex-1 flex items-center justify-center gap-2 py-2 px-3" data-id="element-1542">
                      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-1543">
                        R
                      </span>
                      <span className="text-xl font-display font-black tracking-wider text-gray-900" data-id="element-1544">
                        84922
                      </span>
                    </div>
                  </div>

                  <p className="text-[10px] text-gray-400 mt-2 text-center font-medium" data-id="element-1545">
                    Verified against UAE traffic registry
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN — Docs & Actions */}
          <div className="space-y-6" data-id="element-1546">
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100" data-id="element-1547">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1548">
                Job Documents
              </h3>
              <div className="space-y-3" data-id="element-1549">
                <button className="w-full flex items-center justify-between p-4 rounded-2xl border border-gray-100 hover:border-brand-200 hover:bg-brand-50/50 transition-colors group" data-id="element-1550">
                  <div className="flex items-center gap-3" data-id="element-1551">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center" data-id="element-1552">
                      <FileText className="w-5 h-5" data-id="element-1553" />
                    </div>
                    <div className="text-left" data-id="element-1554">
                      <p className="font-medium text-gray-900 group-hover:text-brand-700" data-id="element-1555">
                        Digital Waybill
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1556">
                        Generated automatically
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-brand-500" data-id="element-1557" />
                </button>
                <button className="w-full flex items-center justify-between p-4 rounded-2xl border border-gray-100 hover:border-brand-200 hover:bg-brand-50/50 transition-colors group" data-id="element-1558">
                  <div className="flex items-center gap-3" data-id="element-1559">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center" data-id="element-1560">
                      <ShieldCheck className="w-5 h-5" data-id="element-1561" />
                    </div>
                    <div className="text-left" data-id="element-1562">
                      <p className="font-medium text-gray-900 group-hover:text-brand-700" data-id="element-1563">
                        Insurance Cert
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1564">
                        Active • AED 50k cover
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-brand-500" data-id="element-1565" />
                </button>
                <button className="w-full flex items-center justify-between p-4 rounded-2xl border border-gray-100 hover:border-brand-200 hover:bg-brand-50/50 transition-colors group opacity-50 cursor-not-allowed" data-id="element-1566">
                  <div className="flex items-center gap-3" data-id="element-1567">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center" data-id="element-1568">
                      <Camera className="w-5 h-5" data-id="element-1569" />
                    </div>
                    <div className="text-left" data-id="element-1570">
                      <p className="font-medium text-gray-900" data-id="element-1571">
                        Proof of Delivery
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1572">
                        Pending completion
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <div className="bg-gray-900 rounded-3xl p-6 text-white shadow-xl" data-id="element-1573">
              <div className="flex items-center gap-2 mb-2" data-id="element-1574">
                <Sparkles className="w-5 h-5 text-brand-400" data-id="element-1575" />
                <h3 className="font-bold" data-id="element-1576">AI Operations Agent</h3>
              </div>
              <p className="text-sm text-gray-400 mb-4 leading-relaxed" data-id="element-1577">
                I'm monitoring this move. Traffic is light on E11, so ETA is
                highly accurate. I'll notify you when loading begins.
              </p>
              <button className="w-full py-3 bg-white/10 hover:bg-white/20 rounded-xl text-sm font-medium transition-colors backdrop-blur-sm" data-id="element-1578">
                Ask a question
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* FULLSCREEN LIVE MAP MODAL */}
      <AnimatePresence data-id="element-1579">
        {mapExpanded && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setMapExpanded(false)} data-id="element-1580">
            <motion.div initial={{
          scale: 0.96,
          y: 12
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.96,
          y: 12
        }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-3xl w-full max-w-5xl h-[80vh] overflow-hidden shadow-2xl" data-id="element-1581">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=2000')] bg-cover bg-center" data-id="element-1582" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" data-id="element-1583" />

              {/* Header */}
              <div className="absolute top-4 left-4 right-4 flex items-start justify-between z-10" data-id="element-1584">
                <div className="flex items-center gap-2 bg-red-500/95 backdrop-blur text-white px-3 py-1.5 rounded-full shadow-lg" data-id="element-1585">
                  <span className="relative flex h-2 w-2" data-id="element-1586">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" data-id="element-1587" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-white" data-id="element-1588" />
                  </span>
                  <span className="text-[11px] font-bold uppercase tracking-wider" data-id="element-1589">
                    Live tracking
                  </span>
                </div>
                <button onClick={() => setMapExpanded(false)} className="w-10 h-10 rounded-full bg-white/95 hover:bg-white flex items-center justify-center shadow-lg transition-colors" aria-label="Close map" data-id="element-1590">
                  <X className="w-5 h-5 text-gray-900" data-id="element-1591" />
                </button>
              </div>

              {/* Truck position */}
              <div className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 z-10" data-id="element-1592">
                <div className="relative" data-id="element-1593">
                  <div className="absolute inset-0 w-24 h-24 -m-4 rounded-full bg-brand-500/30 animate-ping" data-id="element-1594" />
                  <div className="relative w-16 h-16 rounded-full bg-white shadow-2xl border-2 border-brand-500 flex items-center justify-center" data-id="element-1595">
                    <Truck className="w-8 h-8 text-brand-600" data-id="element-1596" />
                  </div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 whitespace-nowrap bg-gray-900 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg" data-id="element-1597">
                    Ahmed · DXB R 84922 · 1.4 km away
                  </div>
                </div>
              </div>

              {/* Bottom card */}
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 z-10 shadow-xl flex items-center justify-between gap-4" data-id="element-1598">
                <div className="flex items-center gap-3" data-id="element-1599">
                  <img src="https://i.pravatar.cc/100?img=11" alt="Driver" className="w-12 h-12 rounded-full object-cover" data-id="element-1600" />
                  <div data-id="element-1601">
                    <p className="font-bold text-gray-900 text-sm" data-id="element-1602">
                      Ahmed Hassan
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1603">
                      ETA 12 mins · 1.4 km away
                    </p>
                  </div>
                </div>
                <div className="flex gap-2" data-id="element-1604">
                  <button className="w-11 h-11 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700" data-id="element-1605">
                    <MessageSquare className="w-4 h-4" data-id="element-1606" />
                  </button>
                  <a href={`tel:${driverPhone.replace(/\s/g, '')}`} className="px-4 h-11 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center text-white shadow-md shadow-green-500/30 gap-2" data-id="element-1607">
                    <Phone className="w-4 h-4" data-id="element-1608" />
                    <span className="text-sm font-bold" data-id="element-1609">Call driver</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}